import React, { useState, useEffect } from "react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import userService from "../../services/userService";
import { FaSearch, FaExclamationTriangle, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import "../../styles/admin/UserLicenses.css";

function UserLicenses() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const usersData = await userService.getAllUsers();
            // Filter for freelancers/partners only if needed, but usually license is for them
            const partners = usersData.filter(u => u.role === 'freelancer');
            setUsers(partners);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateDaysRemaining = (expiryDate) => {
        if (!expiryDate) return null;
        const expiry = new Date(expiryDate);
        const now = new Date();
        const diffTime = expiry - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.licenseKey?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="user-licenses-page">
            <AdminSidebar />
            <div className="licenses-content">
                <div className="licenses-header">
                    <h2>User Licenses</h2>
                    <div className="licenses-search-bar">
                        <FaSearch />
                        <input
                            type="text"
                            placeholder="Search by name, email, or license key..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading licenses...</div>
                ) : (
                    <div className="licenses-table-container">
                        <table className="licenses-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>License Key</th>
                                    <th>Expiry Date</th>
                                    <th>Status</th>
                                    <th>Days Remaining</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>No users found matching your search.</td>
                                    </tr>
                                ) : (
                                    filteredUsers.map(user => {
                                        const daysRemaining = calculateDaysRemaining(user.licenseExpiry);
                                        const isExpired = daysRemaining !== null && daysRemaining <= 0;
                                        const isLow = daysRemaining !== null && daysRemaining <= 7 && !isExpired;

                                        return (
                                            <tr key={user.id}>
                                                <td>
                                                    <div style={{ fontWeight: '600', fontSize: '1rem' }}>{user.name}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '2px' }}>{user.email}</div>
                                                </td>
                                                <td style={{ fontFamily: 'SF Mono, monospace', letterSpacing: '0.5px', fontSize: '0.9rem' }}>
                                                    {user.licenseKey || <span style={{ color: '#ccc', fontStyle: 'italic' }}>No License</span>}
                                                </td>
                                                <td>
                                                    {user.licenseExpiry ? new Date(user.licenseExpiry).toLocaleDateString() : <span style={{ color: '#ccc' }}>N/A</span>}
                                                </td>
                                                <td>
                                                    {user.licenseExpiry ? (
                                                        isExpired ? (
                                                            <span className="license-status-badge" style={{ background: '#fff1f0', color: '#cf1322', border: '1px solid #ffa39e' }}>
                                                                <FaTimesCircle style={{ marginRight: '6px' }} /> Expired
                                                            </span>
                                                        ) : (
                                                            <span className="license-status-badge" style={{ background: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f' }}>
                                                                <FaCheckCircle style={{ marginRight: '6px' }} /> Active
                                                            </span>
                                                        )
                                                    ) : (
                                                        <span className="license-status-badge" style={{ background: '#f5f5f5', color: '#999', border: '1px solid #e0e0e0' }}>
                                                            No License
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {user.licenseExpiry ? (
                                                        <div className="days-remaining" style={{
                                                            color: isExpired ? '#cf1322' : isLow ? '#d46b08' : '#389e0d'
                                                        }}>
                                                            {isLow && <FaExclamationTriangle />}
                                                            {daysRemaining <= 0 ? "0 Days" : `${daysRemaining} Days`}
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#ccc' }}>-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default UserLicenses;
